"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  type GameState,
  type Player,
  type WallOrientation,
  WALLS_PER_PLAYER,
  applyAction,
  canPlaceWall,
  createInitialState,
  getPawnMoves,
  shortestPathLength,
} from "./engine";
import { chooseAiAction } from "./ai";
import { recordMatchResult } from "@/lib/quoridor-session";

// 뷰포트 폭에 맞춰 줄어드는 칸/갭 크기 (작은 화면에서 가로 스크롤 없이 보드 전체가 보이도록)
const CELL_TRACK = "clamp(26px, 8vw, 40px)";
const GAP_TRACK = "clamp(5px, 1.6vw, 8px)";
// 교차점(벽 놓기) 버튼의 실제 터치 영역: 화면에 보이는 갭(6~8px)보다 훨씬 크게 잡아
// 모바일에서 손가락으로도 정확히 누를 수 있게 함. 인접 칸은 버튼이 아닌 장식용
// div라 겹쳐도 오작동하지 않음.
const INTERSECTION_HIT_SIZE = 26;
const AI_THINK_MS = 500;

function posKey(row: number, col: number) {
  return `${row},${col}`;
}

export default function QuoridorGame() {
  const [state, setState] = useState<GameState>(() => createInitialState(0));
  const [history, setHistory] = useState<GameState[]>([]);
  const [orientation, setOrientation] = useState<WallOrientation>("h");
  const [firstPlayer, setFirstPlayer] = useState<Player>(0);
  const [hintsVisible, setHintsVisible] = useState(false);
  const aiThinking = state.turn === 1 && state.winner === null;

  const humanMoves = useMemo(() => {
    if (state.turn !== 0 || state.winner !== null) return [];
    return getPawnMoves(state, 0);
  }, [state]);

  const humanMoveSet = useMemo(
    () => new Set(humanMoves.map((m) => posKey(m.row, m.col))),
    [humanMoves],
  );

  useEffect(() => {
    if (!aiThinking) return;
    const timer = setTimeout(() => {
      setState((s) => {
        if (s.turn !== 1 || s.winner !== null) return s;
        setHistory((h) => [...h, s]);
        const action = chooseAiAction(s, 1);
        return applyAction(s, action);
      });
    }, AI_THINK_MS);
    return () => clearTimeout(timer);
  }, [aiThinking]);

  // 게임이 끝나면 로그인 계정에 전적을 한 번만 기록한다(비로그인이면 서버 액션이
  // 조용히 무시함). 재시작하면 다음 종료 때 다시 기록되도록 플래그를 리셋한다.
  const recordedRef = useRef(false);
  useEffect(() => {
    if (state.winner === null) {
      recordedRef.current = false;
      return;
    }
    if (recordedRef.current) return;
    recordedRef.current = true;
    recordMatchResult(
      state.winner === 0 ? "human" : "ai",
      firstPlayer === 0 ? "human" : "ai",
      WALLS_PER_PLAYER - state.wallsLeft[0],
      WALLS_PER_PLAYER - state.wallsLeft[1],
    ).catch(() => {});
  }, [state, firstPlayer]);

  function handleCellClick(row: number, col: number) {
    if (state.turn !== 0 || state.winner !== null) return;
    if (!humanMoveSet.has(posKey(row, col))) return;
    setState((s) => {
      setHistory((h) => [...h, s]);
      return applyAction(s, { type: "move", to: { row, col } });
    });
  }

  function handleIntersectionClick(r: number, c: number) {
    if (state.turn !== 0 || state.winner !== null) return;
    if (!canPlaceWall(state, orientation, r, c, 0)) return;
    setState((s) => {
      setHistory((h) => [...h, s]);
      return applyAction(s, { type: "wall", orientation, r, c });
    });
  }

  function handleUndo() {
    setHistory((h) => {
      if (h.length === 0) return h;
      // AI가 자동으로 응대하기 전, 내가 실제로 결정을 내렸던 가장 최근
      // 시점(내 차례였던 상태)까지 되돌린다.
      let idx = h.length - 1;
      while (idx > 0 && h[idx].turn !== 0) idx--;
      setState(h[idx]);
      return h.slice(0, idx);
    });
  }

  function handleRestart() {
    setState(createInitialState(firstPlayer));
    setHistory([]);
  }

  const trackSizes = Array.from({ length: 17 }, (_, i) =>
    i % 2 === 0 ? CELL_TRACK : GAP_TRACK,
  ).join(" ");

  const boardItems: ReactNode[] = [];

  // 9x9 칸: 말 표시 + 이동 가능 칸 하이라이트
  for (let i = 0; i < 9; i++) {
    for (let j = 0; j < 9; j++) {
      const isHuman = state.pawns[0].row === i && state.pawns[0].col === j;
      const isAi = state.pawns[1].row === i && state.pawns[1].col === j;
      const isMoveTarget = humanMoveSet.has(posKey(i, j));
      boardItems.push(
        <button
          key={`cell-${i}-${j}`}
          type="button"
          disabled={!isMoveTarget}
          onClick={() => handleCellClick(i, j)}
          style={{ gridRow: 2 * i + 1, gridColumn: 2 * j + 1 }}
          className={`flex touch-manipulation items-center justify-center rounded-sm border transition ${
            isMoveTarget
              ? "cursor-pointer border-blue-400 bg-blue-400/10 hover:bg-blue-400/20 active:bg-blue-400/30"
              : "border-foreground/10 bg-foreground/[0.02]"
          }`}
        >
          {isHuman && <span className="h-5 w-5 rounded-full bg-blue-500" />}
          {isAi && <span className="h-5 w-5 rounded-full bg-red-500" />}
        </button>,
      );
    }
  }

  // 가로 갭: 가로 벽 세그먼트 표시 (row-gap i, col j)
  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 9; j++) {
      const blocked = state.blockedH.has(posKey(i, j));
      boardItems.push(
        <div
          key={`hgap-${i}-${j}`}
          style={{ gridRow: 2 * i + 2, gridColumn: 2 * j + 1 }}
          className={`rounded-sm ${blocked ? "bg-amber-600" : ""}`}
        />,
      );
    }
  }

  // 세로 갭: 세로 벽 세그먼트 표시 (row i, col-gap j)
  for (let i = 0; i < 9; i++) {
    for (let j = 0; j < 8; j++) {
      const blocked = state.blockedV.has(posKey(i, j));
      boardItems.push(
        <div
          key={`vgap-${i}-${j}`}
          style={{ gridRow: 2 * i + 1, gridColumn: 2 * j + 2 }}
          className={`rounded-sm ${blocked ? "bg-amber-600" : ""}`}
        />,
      );
    }
  }

  // 8x8 교차점: 벽 놓기 버튼
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const legal =
        state.turn === 0 &&
        state.winner === null &&
        canPlaceWall(state, orientation, r, c, 0);
      boardItems.push(
        <button
          key={`x-${r}-${c}`}
          type="button"
          disabled={!legal}
          onClick={() => handleIntersectionClick(r, c)}
          style={{
            gridRow: 2 * r + 2,
            gridColumn: 2 * c + 2,
            width: INTERSECTION_HIT_SIZE,
            height: INTERSECTION_HIT_SIZE,
            justifySelf: "center",
            alignSelf: "center",
          }}
          className="group z-10 flex touch-manipulation items-center justify-center rounded-full bg-transparent"
        >
          <span
            className={`rounded-full transition ${
              orientation === "h" ? "h-1.5 w-4" : "h-4 w-1.5"
            } ${
              legal
                ? "cursor-pointer bg-foreground/30 group-hover:bg-foreground/60 group-active:bg-foreground/70"
                : "bg-transparent"
            }`}
          />
        </button>,
      );
    }
  }

  const statusText =
    state.winner !== null
      ? state.winner === 0
        ? "🎉 승리했습니다!"
        : "AI가 승리했습니다."
      : state.turn === 0
        ? "당신 차례"
        : "AI 생각 중...";

  const humanHint = shortestPathLength(state, 0);
  const aiHint = shortestPathLength(state, 1);
  const fmtHint = (n: number) => (Number.isFinite(n) ? `${n}수` : "—");

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="text-sm font-medium">{statusText}</div>

      <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center">
        <span className="text-foreground/60">선공</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFirstPlayer(0)}
            className={`flex-1 touch-manipulation rounded-md border px-4 py-2.5 transition sm:flex-none ${
              firstPlayer === 0
                ? "border-foreground bg-foreground text-background"
                : "border-foreground/20 hover:border-foreground/40"
            }`}
          >
            내가 선공
          </button>
          <button
            type="button"
            onClick={() => setFirstPlayer(1)}
            className={`flex-1 touch-manipulation rounded-md border px-4 py-2.5 transition sm:flex-none ${
              firstPlayer === 1
                ? "border-foreground bg-foreground text-background"
                : "border-foreground/20 hover:border-foreground/40"
            }`}
          >
            AI가 선공
          </button>
        </div>
        <span className="text-xs text-foreground/40">
          (재시작 시 적용)
        </span>
      </div>

      <div className="flex gap-2 text-sm">
        <button
          type="button"
          onClick={handleUndo}
          disabled={history.length === 0}
          className="flex-1 touch-manipulation rounded-md border border-foreground/20 px-4 py-2.5 transition hover:border-foreground/40 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-foreground/20 sm:flex-none"
        >
          되돌리기
        </button>
        <button
          type="button"
          onClick={handleRestart}
          className="flex-1 touch-manipulation rounded-md border border-foreground/20 px-4 py-2.5 transition hover:border-foreground/40 sm:flex-none"
        >
          재시작
        </button>
      </div>

      <div className="flex items-center gap-4 text-sm text-foreground/60">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 shrink-0 rounded-full bg-blue-500" /> 나: 벽{" "}
          {state.wallsLeft[0]}개
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 shrink-0 rounded-full bg-red-500" /> AI: 벽{" "}
          {state.wallsLeft[1]}개
        </span>
      </div>

      <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center">
        <span className="text-foreground/60">벽 방향</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setOrientation("h")}
            className={`flex-1 touch-manipulation rounded-md border px-4 py-2.5 transition sm:flex-none ${
              orientation === "h"
                ? "border-foreground bg-foreground text-background"
                : "border-foreground/20 hover:border-foreground/40"
            }`}
          >
            가로
          </button>
          <button
            type="button"
            onClick={() => setOrientation("v")}
            className={`flex-1 touch-manipulation rounded-md border px-4 py-2.5 transition sm:flex-none ${
              orientation === "v"
                ? "border-foreground bg-foreground text-background"
                : "border-foreground/20 hover:border-foreground/40"
            }`}
          >
            세로
          </button>
        </div>
      </div>

      <div className="mx-auto flex w-max max-w-full flex-col gap-2">
        <button
          type="button"
          onClick={() => setHintsVisible((v) => !v)}
          className="w-full touch-manipulation rounded-md border border-foreground/20 px-4 py-2.5 text-sm transition hover:border-foreground/40"
        >
          {hintsVisible ? "힌트 숨기기" : "힌트 보기"}
        </button>

        {hintsVisible && (
          <div className="flex w-full items-center justify-center gap-4 rounded-md border border-foreground/10 bg-foreground/[0.02] px-4 py-2.5 text-sm text-foreground/70">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500" />
              나: 승리까지 {fmtHint(humanHint)}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500" />
              AI: 승리까지 {fmtHint(aiHint)}
            </span>
          </div>
        )}

        <div className="overflow-x-auto pb-2">
          <div
            className="mx-auto grid"
            style={{
              gridTemplateColumns: trackSizes,
              gridTemplateRows: trackSizes,
              width: "max-content",
            }}
          >
            {boardItems}
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-foreground/50 sm:text-left">
        칸을 클릭해 이동하고, 점(교차점)을 클릭해 벽을 놓습니다. 파란
        하이라이트가 이동 가능한 칸입니다.
      </p>
    </div>
  );
}
